import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);
// Last-known-user cache so protected pages (which themselves already read
// their data from the IndexedDB offline cache) stay reachable while
// offline, instead of ProtectedRoute bouncing to /login just because the
// "am I logged in" network call couldn't reach the server. This never
// substitutes for real auth - all actual API calls still go through the
// httpOnly session cookies as before, this only affects what the UI shows.
const AUTH_CACHE_KEY = "pmi_cached_user";

function cacheUser(data) {
  try {
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(data));
  } catch {}
}

function readCachedUser() {
  try {
    return JSON.parse(localStorage.getItem(AUTH_CACHE_KEY) || "null");
  } catch {
    return null;
  }
}

function clearCachedUser() {
  try {
    localStorage.removeItem(AUTH_CACHE_KEY);
  } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      cacheUser(data);
    } catch (e) {
      if (!e.response) {
        // Genuinely unreachable (offline), not a real 401 - trust the last
        // known session rather than forcing a logout the server never
        // actually issued.
        setUser(readCachedUser() || false);
        return;
      }
      setUser(false);
      clearCachedUser();
    }
  }, []);

  useEffect(() => {
    checkAuth();
    // Re-validate for real once connectivity returns, in case the session
    // was actually invalidated server-side while this device was offline.
    window.addEventListener("online", checkAuth);
    return () => window.removeEventListener("online", checkAuth);
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, remember_me: rememberMe });
      setUser(data);
      cacheUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      setUser(data);
      cacheUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
      clearCachedUser();
    }
  };

  const logoutAll = async () => {
    try {
      await api.post("/auth/logout-all");
    } finally {
      setUser(false);
      clearCachedUser();
    }
  };

  const contextValue = useMemo(() => ({ user, login, register, logout, logoutAll }), [user]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
